import { Badge } from '@/components/ui';
import { BoostPackageNameEnum, CoinPackageNameEnum, IncognitoPackageNameEnum } from '@/enums';
import { Coins, Trophy, Sparkles, Gem, LucideIcon, EyeOff, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

type PackageConfig = {
    icon: LucideIcon;
    iconColor: string;
    label: string;
};

const coinPackageConfigs: Record<CoinPackageNameEnum, PackageConfig> = {
    [CoinPackageNameEnum.GOLD]: {
        icon: Coins,
        iconColor: 'text-yellow-600',
        label: 'GOLD',
    },
    [CoinPackageNameEnum.SILVER]: {
        icon: Trophy,
        iconColor: 'text-slate-600',
        label: 'SILVER',
    },
    [CoinPackageNameEnum.BRONZE]: {
        icon: Sparkles,
        iconColor: 'text-orange-700',
        label: 'BRONZE',
    },
    [CoinPackageNameEnum.DIAMOND]: {
        icon: Gem,
        iconColor: 'text-blue-600',
        label: 'DIAMOND',
    },
};

const incognitoPackageConfigs: Record<IncognitoPackageNameEnum, PackageConfig> = {
    [IncognitoPackageNameEnum.INCOGNITO_1_MONTH]: {
        icon: EyeOff,
        iconColor: 'text-purple-600',
        label: 'INCOGNITO 1M',
    },
    [IncognitoPackageNameEnum.INCOGNITO_3_MONTHS]: {
        icon: EyeOff,
        iconColor: 'text-purple-600',
        label: 'INCOGNITO 3M',
    },
    [IncognitoPackageNameEnum.INCOGNITO_6_MONTHS]: {
        icon: EyeOff,
        iconColor: 'text-purple-600',
        label: 'INCOGNITO 6M',
    },
    [IncognitoPackageNameEnum.INCOGNITO_12_MONTHS]: {
        icon: EyeOff,
        iconColor: 'text-purple-600',
        label: 'INCOGNITO 12M',
    },
};

const boostPackageConfigs: Record<BoostPackageNameEnum, PackageConfig> = {
    [BoostPackageNameEnum.BOOST_1_HOUR]: {
        icon: Zap,
        iconColor: 'text-amber-500',
        label: 'BOOST 1H',
    },
    [BoostPackageNameEnum.BOOST_6_HOURS]: {
        icon: Zap,
        iconColor: 'text-amber-500',
        label: 'BOOST 6H',
    },
    [BoostPackageNameEnum.BOOST_24_HOURS]: {
        icon: Zap,
        iconColor: 'text-amber-500',
        label: 'BOOST 24H',
    },
};

interface PackageBadgeProps {
    // Allow undefined/null or arbitrary strings – API may introduce new package names
    packageName?: CoinPackageNameEnum | IncognitoPackageNameEnum | BoostPackageNameEnum | string | null;
    className?: string;
}

/**
 * Displays a styled badge for coin package tiers.
 * Transparent background, black text, colored icon.
 */
const PackageBadge = ({ packageName, className }: PackageBadgeProps) => {
    let config: PackageConfig | undefined;

    if (packageName && Object.values(CoinPackageNameEnum).includes(packageName as CoinPackageNameEnum)) {
        config = coinPackageConfigs[packageName as CoinPackageNameEnum];
    } else if (packageName && Object.values(IncognitoPackageNameEnum).includes(packageName as IncognitoPackageNameEnum)) {
        config = incognitoPackageConfigs[packageName as IncognitoPackageNameEnum];
    } else if (packageName && Object.values(BoostPackageNameEnum).includes(packageName as BoostPackageNameEnum)) {
        config = boostPackageConfigs[packageName as BoostPackageNameEnum];
    }

    // If there's no package name at all, show a very minimal fallback
    if (!packageName) {
        return (
            <Badge
                variant="outline"
                className={cn(
                    'bg-transparent text-foreground hover:bg-transparent',
                    'cursor-default',
                    className
                )}
            >
                N/A
            </Badge>
        );
    }

    // If we have a packageName but it's not in our predefined list,
    // show a generic badge with a generic icon and the raw name.
    if (!config) {
        const GenericIcon = Sparkles;

        return (
            <Badge
                variant="outline"
                className={cn(
                    'bg-transparent text-foreground hover:bg-transparent',
                    'cursor-default',
                    className
                )}
            >
                <GenericIcon className="w-3 h-3 mr-2 text-muted-foreground" />
                {String(packageName)}
            </Badge>
        );
    }

    const Icon = config.icon;

    return (
        <Badge
            variant="outline"
            className={cn(
                'bg-transparent text-foreground hover:bg-transparent',
                'cursor-default',
                className
            )}
        >
            <Icon className={cn("w-3 h-3 mr-2", config.iconColor)} />
            {config.label}
        </Badge>
    );
};

export default PackageBadge;
